import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import {
  changePassword,
  confirmPasswordReset,
  deleteMyAccount,
  getAccount,
  getReconcilePreview,
  login,
  logout,
  logoutAll,
  postReconcile,
  requestPasswordReset,
  signup,
} from "@/api/auth";
import { deleteCurrentSession } from "@/api/session";
import { resetEnedisProbeSession } from "@/hooks/useEnedisProbeWarmup";
import type {
  AccountOut,
  ChangePasswordRequest,
  LoginRequest,
  PasswordResetConfirmRequest,
  PasswordResetRequest,
  ReconcileResult,
  SignupRequest,
} from "@/types";

export const ACCOUNT_QUERY_KEY = ["auth", "me"] as const;

/**
 * Lit le compte authentifié courant (`GET /auth/me`). Contrairement à
 * `/sessions/me` (lenient), cet endpoint renvoie 401 `AUTH_REQUIRED` quand
 * aucun compte n'est connecté : c'est un **état nominal** (« pas connecté »),
 * pas une erreur applicative. On le traduit donc en `null` plutôt que de le
 * propager au cache d'erreur.
 */
export async function fetchAccountOrNull(): Promise<AccountOut | null> {
  try {
    return await getAccount();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      return null;
    }
    throw err;
  }
}

/**
 * Compte authentifié courant ou `null`. Sert au strict minimum d'indication
 * d'état connecté (Navbar : afficher « Se déconnecter » vs « Se connecter »).
 *
 * Le header conditionnel riche (menu email, « Mes scénarios »…) est **lot 3**,
 * hors S12-7 — ne pas l'enrichir ici.
 */
export function useAccount(): UseQueryResult<AccountOut | null, unknown> {
  return useQuery<AccountOut | null, unknown>({
    queryKey: ACCOUNT_QUERY_KEY,
    queryFn: fetchAccountOrNull,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * Mutation `POST /auth/signup`. En succès, on **purge tout le cache**
 * (bascule d'identité anon → compte) puis on **écrit** le compte dans le
 * cache (`ACCOUNT_QUERY_KEY`) pour repartir d'un état propre.
 *
 * Point d'extension S12-8 (réconciliation) — les call-sites passent leur
 * propre `onSuccess`/`onError` via les `options` de `mutate`/`mutateAsync` :
 * react-query exécute le `onSuccess` du hook PUIS celui de l'appel. S12-8
 * branchera donc `reconcile/preview` + modale dans le `onSuccess` de la page
 * `Signup` **sans** modifier ce hook (cf. `Signup.tsx`, prop `onSignedUp`).
 */
export function useSignup(): UseMutationResult<
  AccountOut,
  unknown,
  SignupRequest
> {
  const queryClient = useQueryClient();
  return useMutation<AccountOut, unknown, SignupRequest>({
    mutationFn: (body) => signup(body),
    onSuccess: (account) => {
      // Bascule d'identité anon → compte (cf. `useLogin`) : on PURGE le cache
      // pour ne pas laisser traîner les ressources de l'anon. Sur le chemin
      // « refus de migration », le compte n'hérite d'aucune simulation : sans
      // ce purge, le dashboard afficherait encore les simu anonymes en cache.
      queryClient.removeQueries();
      // Transition d'identité : purger aussi l'état owner-scoped hors cache
      // react-query (suivi de session du probe Enedis).
      resetEnedisProbeSession();
      queryClient.setQueryData(ACCOUNT_QUERY_KEY, account);
    },
  });
}

/** Mutation `POST /auth/login`. Écrit le compte dans le cache en succès. */
export function useLogin(): UseMutationResult<
  AccountOut,
  unknown,
  LoginRequest
> {
  const queryClient = useQueryClient();
  return useMutation<AccountOut, unknown, LoginRequest>({
    mutationFn: (body) => login(body),
    onSuccess: (account) => {
      // Bascule d'identité anon → compte : TOUTES les listes scopées owner
      // (sites, simulations…) en cache pointent encore sur l'anon. On PURGE
      // le cache (removeQueries, pas seulement invalidate) pour ne pas afficher
      // — ni même flasher — les simulations anonymes sur le dashboard du compte
      // (au clic elles donnaient un 404, et seul un refresh corrigeait). Puis
      // on ré-amorce le compte pour que la Navbar bascule immédiatement.
      queryClient.removeQueries();
      // Transition d'identité : purger aussi le suivi de session du probe Enedis
      // (état owner-scoped module-level, hors cache react-query).
      resetEnedisProbeSession();
      queryClient.setQueryData(ACCOUNT_QUERY_KEY, account);
    },
  });
}

/**
 * Mutation `POST /auth/logout`. En succès, on remet le compte courant à
 * `null` et on purge le cache (les ressources scopées au compte ne doivent
 * plus s'afficher).
 */
export function useLogout(): UseMutationResult<void, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, void>({
    mutationFn: () => logout(),
    onSuccess: () => {
      queryClient.setQueryData(ACCOUNT_QUERY_KEY, null);
      void queryClient.invalidateQueries();
      // Transition d'identité (→ anon) : purger le suivi de session du probe
      // Enedis pour ne pas exclure du warm-up les sites du prochain compte.
      resetEnedisProbeSession();
    },
  });
}

/**
 * Mutation `POST /auth/logout-all` (S15-2). « Déconnecter tous les appareils » :
 * le backend révoque **tous** les JWT du compte (bump `token_version`) et efface
 * le cookie `sunmatch_session` courant — on est donc déconnecté **ici aussi**.
 * Même réconciliation de cache que `useLogout` (transition d'identité compte →
 * déconnecté) : on remet le compte à `null`, on invalide le cache scopé owner et
 * on purge l'état owner-scoped du probe Enedis. Le call-site enchaîne sur une
 * redirection vers `/`.
 */
export function useLogoutAll(): UseMutationResult<void, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, void>({
    mutationFn: () => logoutAll(),
    onSuccess: () => {
      queryClient.setQueryData(ACCOUNT_QUERY_KEY, null);
      void queryClient.invalidateQueries();
      resetEnedisProbeSession();
    },
  });
}

/**
 * Suppression RGPD art. 17 (« droit à l'effacement ») **identité-aware**.
 *
 * Aiguille vers le bon endpoint selon l'identité courante (lue dans le cache
 * `ACCOUNT_QUERY_KEY` au moment du clic, pas capturée à la création du hook) :
 *
 * - **Compte** connecté → `DELETE /auth/me` : purge le compte + toutes ses
 *   ressources scopées `account_id` (sites, simulations, conso, productions,
 *   imports, tokens Enedis) ; le backend efface `sunmatch_session`.
 * - **Anonyme** (pas de compte) → `DELETE /sessions/me` : purge la session
 *   anonyme + sa cascade.
 *
 * Corrige le bug où Settings **et** le Dashboard appelaient *toujours*
 * `/sessions/me` : pour un compte, ses données (scopées `account_id`)
 * n'étaient jamais supprimées et réapparaissaient au refresh.
 *
 * En succès, on **vide tout le cache** (les ressources de l'identité supprimée
 * ne doivent plus jamais s'afficher) ; le call-site enchaîne sur une
 * redirection vers `/`.
 */
export function useDeleteMyData(): UseMutationResult<void, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, void>({
    mutationFn: () => {
      const account = queryClient.getQueryData<AccountOut | null>(
        ACCOUNT_QUERY_KEY,
      );
      return account ? deleteMyAccount() : deleteCurrentSession();
    },
    onSuccess: () => {
      queryClient.clear();
      // Transition d'identité (suppression) : purger le suivi de session du
      // probe Enedis avec le reste de l'état owner-scoped.
      resetEnedisProbeSession();
    },
  });
}

/**
 * Mutation `POST /auth/me/password` (S13-2 → page profil S13-5). Change le mot
 * de passe du compte authentifié. Le body posté est `{ current_password,
 * new_password }` (la confirmation de saisie est front-only). Au succès (204),
 * le backend ré-émet le cookie `sunmatch_session` (rotation de session,
 * transparent) — **aucune** invalidation de cache n'est nécessaire (le profil
 * `AccountOut` est inchangé par un changement de mot de passe).
 *
 * Les erreurs (403 `INVALID_CURRENT_PASSWORD`, 401 `AUTH_REQUIRED`) sont
 * propagées telles quelles ; le call-site (formulaire profil) les mappe sur le
 * bon champ / la redirection login.
 */
export function useChangePassword(): UseMutationResult<
  void,
  unknown,
  ChangePasswordRequest
> {
  return useMutation<void, unknown, ChangePasswordRequest>({
    mutationFn: (body) => changePassword(body),
  });
}

/**
 * Mutation `POST /auth/password-reset/request` (F2 — S35, § 2.quater).
 *
 * **Aucune réconciliation de cache** : demander un lien ne change **rien** à
 * l'identité courante ni à aucune ressource. Succès = 202 **sans corps**,
 * **identique** que le compte existe, soit Google-only, ou n'existe pas — il
 * n'y a **rien à lire** dans la réponse, et le call-site ne doit afficher que la
 * confirmation générique (anti-énumération C7c). Le 429 est propagé au
 * call-site, qui a son propre message.
 */
export function useRequestPasswordReset(): UseMutationResult<
  void,
  unknown,
  PasswordResetRequest
> {
  return useMutation<void, unknown, PasswordResetRequest>({
    mutationFn: (body) => requestPasswordReset(body),
  });
}

/**
 * Mutation `POST /auth/password-reset/confirm` (F2 — S35, § 2.quater).
 *
 * **C'est une transition d'identité, et elle est facile à manquer** : le
 * backend bumpe `token_version` (S15-2) → **toutes** les sessions antérieures
 * sont révoquées, **y compris celle de cet onglet** si l'utilisateur y était
 * connecté (cas réel : « je change mon mot de passe par e-mail depuis le
 * navigateur où je suis déjà connecté »). Le cookie `sunmatch_session` survit
 * mais son JWT est **mort** : sans réconciliation, la Navbar continuerait
 * d'afficher le compte et le dashboard ses ressources en cache, jusqu'au
 * premier 401. On applique donc **exactement** la réconciliation de `useLogout`
 * (compte → déconnecté) : compte à `null`, invalidation du cache scopé owner,
 * purge de l'état owner-scoped du probe Enedis.
 *
 * **Pas d'auto-login** (ADR-018 D6, 204 sans cookie) : le call-site renvoie vers
 * `/login`, ce qui vérifie au passage que le nouveau mot de passe fonctionne.
 * Les erreurs (400/410 jeton, 429) sont propagées telles quelles au call-site.
 */
export function useConfirmPasswordReset(): UseMutationResult<
  void,
  unknown,
  PasswordResetConfirmRequest
> {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, PasswordResetConfirmRequest>({
    mutationFn: (body) => confirmPasswordReset(body),
    onSuccess: () => {
      queryClient.setQueryData(ACCOUNT_QUERY_KEY, null);
      void queryClient.invalidateQueries();
      resetEnedisProbeSession();
    },
  });
}

/**
 * Récupère la prévisualisation de réconciliation (`GET
 * /auth/reconcile/preview`). Utilisée ponctuellement après un signup réussi
 * (S12-8) pour décider d'afficher ou non la modale §E1.6 — pas d'un `useQuery`
 * persistant : on lit la valeur une fois, à la demande. La fonction wrapper est
 * donc réexposée telle quelle (cf. `Signup` qui l'appelle dans `onSignedUp`).
 */
export { getReconcilePreview };

/**
 * Mutation `POST /auth/reconcile` (S12-8). Rattache les ressources de l'anon
 * courant au compte authentifié **sur confirmation explicite** (modale §E1.6).
 * Au commit, le backend purge le cookie `sunmatch_anon` ; on invalide donc tout
 * le cache pour que les ressources nouvellement rattachées (sites, simulations)
 * soient re-fetchées avec la bonne identité d'owner.
 */
export function useReconcile(): UseMutationResult<
  ReconcileResult,
  unknown,
  void
> {
  const queryClient = useQueryClient();
  return useMutation<ReconcileResult, unknown, void>({
    mutationFn: () => postReconcile(),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}
